import React from "react";
import moment from "moment";
import { DownloadNecessaryDocUploadItem } from "../../../utility/types/common";
import { Flex, View } from "@amzn/stencil-react-components/layout";
import { H5, H6, Text } from "@amzn/stencil-react-components/text";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { IconCheckCircleFill, IconDownload } from "@amzn/stencil-react-components/icons";
import { HVH_COLORS } from "../../../config/palette";
import { useChecklistArb } from "../../../hooks/use-arb";
import { Status, StatusIndicator } from "@amzn/stencil-react-components/status-indicator";

interface DownloadDocumentItemProps {
  doc: DownloadNecessaryDocUploadItem;
  handleDownloadFromLink: (downloadedDoc: DownloadNecessaryDocUploadItem) => void;
}

const DownloadDocumentItem = ({ doc, handleDownloadFromLink }: DownloadDocumentItemProps) => {
  const { documentName, downloadedDate, isDownloadedAtCurrentSession, downloadLink, errorMessage } = doc;
  const { formatMessage, getMessage } = useChecklistArb();

  const renderDownloadedDescription = () => {
    return (
      downloadedDate && (
        <Text fontSize="T100" data-testid="docUpload-item-downloadedAt">
          {formatMessage("Checklist-docUpload-download-docItem-downloadedAt", {
            date: moment(new Date()).format("YYYY-MM-DD"),
            hour: moment(new Date()).format("HH:mm"),
          })}
        </Text>
      )
    );
  };

  const renderActionButtons = () => {
    if (downloadedDate) {
      return (
        <Flex flexDirection="row" alignItems="center" data-testid="docUpload-item-showCheckWithDownloadButton">
          <IconCheckCircleFill color={HVH_COLORS.GREEN_70} margin={{ right: ".5rem" }} />
          {!isDownloadedAtCurrentSession && (
            <a href={downloadLink} target="_blank" rel="noreferrer" onClick={() => handleDownloadFromLink(doc)}>
              <Button
                icon={<IconDownload color={HVH_COLORS.PRIMARY_70} title="Discard" />}
                variant={ButtonVariant.Tertiary}
              />
            </a>
          )}
        </Flex>
      );
    }

    if (!isDownloadedAtCurrentSession) {
      return (
        <a href={downloadLink} target="_blank" rel="noreferrer" onClick={() => handleDownloadFromLink(doc)}>
          <Button
            data-testid="downloadDocUploadNecessaryDocumentButton"
            icon={<IconDownload color={HVH_COLORS.NEUTRAL_0} title="Discard" />}
            variant={ButtonVariant.Primary}
          />
        </a>
      );
    }
  };

  const renderError = () => {
    return (
      errorMessage && (
        <View margin={{ top: ".5rem" }} data-testid="docUpload-item-error">
          <StatusIndicator
            messageText={getMessage("Checklist-docUpload-downloadPage-item-error", "Download the document to continue")}
            status={Status.Negative}
          />
        </View>
      )
    );
  };

  return (
    <>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        maxWidth={"450px"}
        padding={"1rem 2rem"}
        margin={{ top: "1rem" }}
        border={`1px solid ${errorMessage ? HVH_COLORS.RED_70 : "gainsboro"}`}
      >
        <View>
          <H5>{documentName}</H5>
          {renderDownloadedDescription()}
        </View>
        {renderActionButtons()}
      </Flex>
      {renderError()}
    </>
  );
};

export default DownloadDocumentItem;
